//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Terms of Use Dialog Composable
 *
 * Manages terms of use dialog state and acceptance.
 * Follows same pattern as other validation dialogs.
 *
 * Responsibilities:
 * - Load and cache terms of use content
 * - Manage dialog open/close state
 * - Handle terms acceptance/rejection
 * - Track scroll position for "read to bottom" requirement
 * - Reload terms on locale changes
 * - Persist acceptance state to sessionStorage
 *
 * @param key - Unique key for state isolation
 */
export const useTermsDialog = (key: string = "default") => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  const { locale } = useI18n();
  const { fetchTermsOfUse } = useLaboratory();
  const { handleError } = useErrorBoundary();

  //
  // ============================================================================
  // STATE
  // ============================================================================
  //

  const isOpen = useState<boolean>(`terms.${key}.isOpen`, () => false);
  const loading = useState<boolean>(`terms.${key}.loading`, () => false);
  const termsContent = useState<string | null>(`terms.${key}.content`, () => null);
  const agreeToTerms = useSessionState<boolean>(`terms.${key}.agreed`, true);
  const scrolledToBottom = useState<boolean>(`terms.${key}.scrolled`, () => false);

  //
  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================
  //

  /**
   * Can agree to terms (only after scrolling to bottom)
   */
  const canAgree = computed(() => scrolledToBottom.value);

  /**
   * Component props for dialog binding
   */
  const componentProps = computed(() => ({
    isOpen: isOpen.value,
    loading: loading.value,
    termsContent: termsContent.value,
    agreeToTerms: agreeToTerms.value,
    canAgree: canAgree.value,
    onAgree: handleAgree,
    onDisagree: handleDisagree,
    onScroll: handleScroll,
    onClose: close,
  }));

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Open dialog and load terms if needed
   */
  const open = async (): Promise<void> => {
    isOpen.value = true;
    scrolledToBottom.value = false;

    // Load terms if not cached or locale changed
    if (!termsContent.value) {
      await loadTerms();
    }
  };

  /**
   * Close dialog
   */
  const close = (): void => {
    isOpen.value = false;
  };

  /**
   * Load terms of use for current locale
   */
  const loadTerms = async (): Promise<void> => {
    loading.value = true;

    try {
      const terms = await fetchTermsOfUse(locale.value, null, false);
      if (terms) {
        termsContent.value = typeof terms === "string" ? terms : terms.content;
      }
    } catch (err) {
      handleError(err, {
        operation: "loadTerms",
        showToast: true,
      });
    } finally {
      loading.value = false;
    }
  };

  /**
   * Handle scroll event (enable agree button when scrolled to bottom)
   */
  const handleScroll = (scrolledToEnd: boolean): void => {
    scrolledToBottom.value = scrolledToEnd;
  };

  /**
   * Handle agree action
   */
  const handleAgree = (): void => {
    agreeToTerms.value = true;
    close();
  };

  /**
   * Handle disagree action
   */
  const handleDisagree = (): void => {
    agreeToTerms.value = false;
    close();
  };

  //
  // ============================================================================
  // RETURN PUBLIC API
  // ============================================================================
  //

  return {
    // State (readonly)
    isOpen: readonly(isOpen),
    loading: readonly(loading),
    termsContent: readonly(termsContent),
    agreeToTerms,

    // Computed
    canAgree,
    componentProps,

    // Methods
    open,
    close,
    loadTerms,
    handleAgree,
    handleDisagree,
    handleScroll,
  };
};
